import { useState, useCallback } from 'react';
import { Active, Over, DragEndEvent } from '@dnd-kit/core';
import { useTodoContext } from '../contexts/TodoContext';
import { canAddChildToNode } from '../utils/tree';

/**
 * Represents a drag and drop operation between todo nodes
 */
export interface DragOperation {
  /** ID of the node being dragged */
  sourceId: string;
  /** ID of the target node (drop destination) */
  targetId: string;
  /** Type of operation: 'reorder' for same-parent reordering, 'move' for cross-parent moves */
  operation: 'reorder' | 'move';
}

/**
 * What dropping the dragged node onto a given target will do:
 * reorder among siblings, re-parent the dragged subtree, or nothing.
 */
export type DropIndicator = 'reorder' | 'reparent' | null;

/**
 * Optional callbacks for surfacing user feedback during drag operations.
 */
export interface UseDragDropOptions {
  /** Called when a requested move cannot be performed (e.g. would create a cycle). */
  onMoveBlocked?: (message: string) => void;
}

/**
 * Hook to manage drag and drop operations for todo nodes.
 *
 * Supports two kinds of drops:
 * - Reordering siblings within the same parent (drop onto a sibling).
 * - Re-parenting a node and its entire subtree under another node
 *   (drop onto any non-descendant node), as long as the result stays a valid tree.
 */
export function useDragDrop(options?: UseDragDropOptions) {
  const { tree, reorderChildren, moveItem } = useTodoContext();
  const onMoveBlocked = options?.onMoveBlocked;
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [draggedOver, setDraggedOver] = useState<string | null>(null);

  const findParentId = useCallback((childId: string): string | null => {
    for (const [parentId, parentItem] of Object.entries(tree)) {
      if (parentItem.children.includes(childId)) {
        return parentId;
      }
    }
    return null;
  }, [tree]);

  /**
   * Checks if a node is a descendant of another node to prevent circular dependencies
   * Uses breadth-first search to traverse the tree structure
   */
  const isDescendant = useCallback((ancestorId: string, nodeId: string): boolean => {
    const queue = [ancestorId];
    const visited = new Set<string>();

    while (queue.length > 0) {
      const currentId = queue.shift()!;

      // Prevent infinite loops in malformed trees
      if (visited.has(currentId)) {
        continue;
      }
      visited.add(currentId);

      const currentItem = tree[currentId];
      if (!currentItem) {
        continue;
      }

      // Direct child relationship found
      if (currentItem.children.includes(nodeId)) {
        return true;
      }

      // Add children to search queue for deeper traversal
      queue.push(...currentItem.children);
    }

    return false;
  }, [tree]);

  const isValidMove = useCallback((sourceId: string, targetId: string): boolean => {
    // Prevent moving an item into itself
    if (sourceId === targetId) {
      return false;
    }

    // Prevent moving an item into its descendants
    if (isDescendant(sourceId, targetId)) {
      return false;
    }

    return true;
  }, [isDescendant]);

  /**
   * Reorders siblings within the same parent based on drag-and-drop operation
   * Handles the complex logic of determining drop position relative to target
   */
  const reorderSiblings = useCallback((sourceId: string, targetId: string, parentId: string) => {
    const parent = tree[parentId];
    if (!parent) return;

    const children = [...parent.children];
    const sourceIndex = children.indexOf(sourceId);
    const targetIndex = children.indexOf(targetId);

    if (sourceIndex === -1 || targetIndex === -1) return;

    // Step 1: Remove the source item from its current position
    // This affects all subsequent indices, so we need to recalculate
    children.splice(sourceIndex, 1);

    // Step 2: Find the target's new index after source removal
    let newTargetIndex = children.indexOf(targetId);
    if (newTargetIndex === -1) return; // Sanity check - target should still exist

    // Step 3: Determine drop position based on drag direction
    // This creates intuitive behavior where dropped items appear where the user expects
    if (sourceIndex < targetIndex) {
      // Moving down in the list: place AFTER the target
      // User dragged from above, so they expect to drop below the target
      children.splice(newTargetIndex + 1, 0, sourceId);
    } else {
      // Moving up in the list: place BEFORE the target
      // User dragged from below, so they expect to drop above the target
      children.splice(newTargetIndex, 0, sourceId);
    }

    // Step 4: Update the parent's children array through the context
    reorderChildren(parentId, children);
  }, [tree, reorderChildren]);

  const handleDragStart = useCallback((event: { active: Active }) => {
    setDraggedItem(event.active.id as string);
  }, []);

  const handleDragOver = useCallback((event: { over: Over | null }) => {
    setDraggedOver(event.over?.id as string || null);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    setDraggedItem(null);
    setDraggedOver(null);

    if (!over || active.id === over.id) {
      return;
    }

    const sourceId = active.id as string;
    const targetId = over.id as string;

    // Find the parent of both items
    const sourceParentId = findParentId(sourceId);
    const targetParentId = findParentId(targetId);

    // The root node has no parent and must never be moved.
    if (!sourceParentId) {
      return;
    }

    // Guard against any move that would break the tree (into itself or a descendant).
    if (!isValidMove(sourceId, targetId)) {
      return;
    }

    // Same parent: reorder siblings (preserves the existing in-column ordering UX).
    if (sourceParentId === targetParentId) {
      reorderSiblings(sourceId, targetId, sourceParentId);
      return;
    }

    // Cross-parent: re-parent the dragged node (and its subtree) under the target.
    // Dropping onto the node that is already the parent is a no-op.
    if (targetId === sourceParentId) {
      return;
    }

    // The target must be able to accept children (leaf nodes with a time estimate cannot).
    if (!canAddChildToNode(tree, targetId)) {
      onMoveBlocked?.('Cannot move a task under one with a time estimate. Remove the estimate first.');
      return;
    }

    moveItem(sourceId, targetId);
  }, [tree, findParentId, isValidMove, reorderSiblings, moveItem, onMoveBlocked]);

  const isDraggedOver = useCallback((nodeId: string): boolean => {
    return draggedOver === nodeId;
  }, [draggedOver]);

  const isDragging = useCallback((nodeId: string): boolean => {
    return draggedItem === nodeId;
  }, [draggedItem]);

  /**
   * Describes what will happen if the current drag is dropped on `nodeId`.
   *
   * Used to render a drop-target affordance so the user can tell, mid-drag,
   * whether a drop will reorder siblings or re-parent the dragged subtree.
   *
   * - `'reorder'`  : drop reorders within the same parent
   * - `'reparent'` : drop moves the dragged node (and subtree) under this node
   * - `null`       : `nodeId` is not the active drop target, or the drop would
   *                  do nothing / be invalid
   */
  const getDropIndicator = useCallback((nodeId: string): DropIndicator => {
    if (!draggedItem || draggedOver !== nodeId || draggedItem === nodeId) {
      return null;
    }

    const sourceParentId = findParentId(draggedItem);
    if (!sourceParentId) return null; // root is never draggable

    // Same parent: reordering siblings.
    if (sourceParentId === findParentId(nodeId)) {
      return 'reorder';
    }

    // Dropping onto the node that is already the parent does nothing.
    if (nodeId === sourceParentId) return null;

    // A re-parent that would create a cycle or target a time-estimated leaf is invalid.
    if (!isValidMove(draggedItem, nodeId) || !canAddChildToNode(tree, nodeId)) {
      return null;
    }

    return 'reparent';
  }, [tree, draggedItem, draggedOver, findParentId, isValidMove]);

  return {
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    isDraggedOver,
    isDragging,
    getDropIndicator,
    draggedItem,
    draggedOver,
    isValidMove,
  };
}